import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib/utils';
import {
  Button,
  DropdownMenu,
  DropdownMenuDeleteItem,
  DropdownMenuIconButton,
  Text,
} from '@/shared/ui';
import { PinIcon } from '@/shared/ui/pin-icon/PinIcon';

type Props = {
  title: string;
  isFavorite: boolean;
  /** 現在開かれているスレッドかどうか（汎用 AI チャットで使用） */
  isActive?: boolean;
  onClick?: () => void;
  onRename?: () => void;
  onToggleFavorite?: () => void;
  onDelete?: () => void;
};

export const ChatHistoryItem = ({
  title,
  isFavorite,
  isActive = false,
  onClick,
  onRename,
  onToggleFavorite,
  onDelete,
}: Props) => {
  const { t } = useTranslation();
  // 開いている間は hover 状態を維持する（背景色維持・3点メニュー継続表示）
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const menuItems = [
    {
      key: 'rename',
      item: <Text>{t('ai.chat-history.menu-rename')}</Text>,
      onClick: onRename,
    },
    {
      key: 'favorite',
      item: (
        <Text>
          {isFavorite ? t('ai.chat-history.menu-unfavorite') : t('ai.chat-history.menu-favorite')}
        </Text>
      ),
      hasBottomSeparator: true,
      onClick: onToggleFavorite,
    },
    {
      key: 'delete',
      item: <DropdownMenuDeleteItem />,
      onClick: onDelete,
    },
  ];

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick?.();
    }
  };

  return (
    // 内側に DropdownMenu のトリガー <button> が入るため、ボタンの入れ子を避ける目的で
    // asChild + <div role="button"> でレンダリングする
    <Button
      variant="ghost"
      asChild
      className={cn(
        'group h-8 w-full justify-start gap-2 px-3 font-normal',
        // ghost variant の hover:bg-stone-50 は薄すぎるためチャット履歴の慣習色で上書き
        'hover:bg-surface-strength2',
        // focus-within: キーボード操作で内部のメニュートリガーにフォーカスが当たった時にも背景を維持
        'focus-within:bg-surface-strength2',
        (isActive || isMenuOpen) && 'bg-surface-strength2',
      )}
    >
      {/* biome-ignore lint/a11y/useSemanticElements: AccordionTrigger 等 <button> 内ネスト回避のため div + role="button" を使用 */}
      <div role="button" tabIndex={0} onClick={onClick} onKeyDown={handleKeyDown}>
        <Text as="span" truncate className="flex-1 text-left text-text-secondary">
          {title}
        </Text>
        {/* biome-ignore lint/a11y/noStaticElementInteractions: イベント伝播停止のためのラッパー */}
        <span
          className="relative inline-flex size-8 shrink-0 items-center justify-center"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {/* ピンアイコン: 固定中の状態表示。hover/フォーカス/メニュー開時は3点リーダーに視覚的に切り替わる */}
          {isFavorite && (
            <PinIcon
              className={cn(
                'size-4 text-text-muted transition-opacity',
                'group-focus-within:opacity-0 group-hover:opacity-0',
                isMenuOpen && 'opacity-0',
              )}
              aria-label={t('ai.chat-history.pinned-status-label')}
            />
          )}
          {/*
            3点リーダー: 常時 DOM 上に存在させ、opacity と pointer-events で表示制御する。
            display:none だとキーボード操作・タッチ環境から到達できなくなるため避ける。
          */}
          <DropdownMenu menuItems={menuItems} onOpenChange={setIsMenuOpen}>
            <DropdownMenuIconButton
              size="none"
              className={cn(
                'absolute inset-0 size-8 transition-opacity',
                'opacity-0 group-focus-within:opacity-100 group-hover:opacity-100',
                'focus-visible:opacity-100',
                isMenuOpen && 'opacity-100',
              )}
              aria-label={t('common.actions.operations-menu', { name: title })}
            />
          </DropdownMenu>
        </span>
      </div>
    </Button>
  );
};
